import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { NavLink, Outlet, useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { getPublicSettings } from '@/services/settings';
import { getMe, setThemePreference } from '@/services/me';
import { useThemeSync } from '@marcianus/design/components';
import { TABS, activeTabFor, headerFor, type TabIcon, ROUTES } from '@/app/routes';
import { useNav, describeLocation } from '@/state/nav.store';
import { env } from '@/lib/env';
import { AuthControls } from './AuthControls';
import { HexisSymbol } from '@marcianus/design/components';
import { Icon, type IconName } from '@marcianus/design/components';
import { assertSingleGoldCta } from '@marcianus/design/components/doctrine';
import HexisPopup, { type HexisTab } from '@/modules/hexis/pages/HexisScreen';
import { getNotificationsSummary } from '@/modules/hexis/services/alertas.api';
import { track } from '@/modules/analytics/analytics.api';

// Ícones das tabs — sistema de ícones canónico da raiz (calendar/nfc/grid).
const TAB_ICON: Record<TabIcon, IconName> = { eventos: 'calendar', tap: 'nfc', descobrir: 'search' };
function TabGlyph({ icon }: { icon: TabIcon }) {
  return <Icon name={TAB_ICON[icon]} size={22} />;
}

// ── Shell único do produto: 3 tabs (Tap ao centro) + Hexis flutuante. ──
export function AppShell() {
  const { pathname, search } = useLocation();

  // Doutrina em runtime: «1 CTA dourado por ecrã». Só DEV, a cada rota — só o DOM
  // sabe o que está MESMO no ecrã (dois btn-gold no mesmo ficheiro podem estar em
  // ramos exclusivos). Ver design-system/doctrine.ts.
  useEffect(() => {
    const t = setTimeout(() => assertSingleGoldCta(pathname), 250);
    return () => clearTimeout(t);
  }, [pathname]);
  const [params, setParams] = useSearchParams();
  const active = activeTabFor(pathname);
  const header = headerFor(pathname);

  // NUMA SPA, MUDAR DE PÁGINA NÃO ANUNCIA NADA. O <title> era fixo: o separador
  // do browser mentia, o histórico ficava indistinguível e um leitor de ecrã
  // não dizia que a página tinha mudado (WCAG 2.4.2). Duas linhas resolvem:
  // o título passa a nomear a rota, e uma região viva di-lo em voz alta.
  const [anuncio, setAnuncio] = useState('');
  useEffect(() => {
    const marca = document.documentElement.dataset.brand || 'Marcianus Experience';
    document.title = `${header.title} · ${marca}`;
    setAnuncio(header.title);
  }, [pathname, header.title]);
  const setNav = useNav((s) => s.set);
  const navigate = useNavigate();

  useQuery({ queryKey: ['settings', 'public'], queryFn: getPublicSettings });

  // Tracking real de page-views (alimenta os dashboards /analytics). Fire-and-forget.
  useEffect(() => { track('pageview', { category: 'navigation', screen: pathname }); }, [pathname]);

  // Verifica o utilizador e força onboarding se incompleto
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: getMe, retry: false });

  // O tema segue a pessoa entre dispositivos (THEME-SYSTEM §5).
  useThemeSync({
    persist: (t) => { void setThemePreference(t).catch(() => {}); },
    serverTheme: me?.preferences?.theme,
  });

  // O sinal é onboarded_at (BD) — full_name não serve: o auth provisiona sempre
  // um nome no 1.º login, por isso «!full_name» nunca disparava em produção.
  // localStorage é só o «saltar neste dispositivo» (Completar depois).
  useEffect(() => {
    if (me && !me.onboarded_at && !localStorage.getItem('marc:onboarding:done')) {
      navigate(ROUTES.user_onboarding, { replace: true });
    }
  }, [me, navigate]);

  const { data: notifSummary } = useQuery({
    queryKey: ['notifications', 'summary'],
    queryFn: getNotificationsSummary,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  // Hexis como overlay glass sobre a tab atual — controlado por ?hexis=chat|alertas
  // (URL + histórico preservados; a tab por baixo mantém-se, como na referência).
  const hexisTab = params.get('hexis') as HexisTab | null;
  const closeHexis = () => setParams((p) => { p.delete('hexis'); return p; });

  // Contexto em tempo real p/ o Hexis — regista tab/subtab (não quando o Hexis
  // está aberto, p/ preservar "de onde vim"). Base p/ recomendações + telemetria.
  useEffect(() => {
    if (new URLSearchParams(search).get('hexis')) return;   // overlay de alertas (sino)
    if (pathname === ROUTES.hexis) return;                   // a rota Hexis NÃO sobrescreve o contexto
    setNav(describeLocation(activeTabFor(pathname) || 'global', pathname, search));
  }, [pathname, search, setNav]);

  // ── HexisFab scroll-aware: encolhe ao scroll-down, re-expande ao scroll-up ou
  //    ao ficar parado (~900ms). Nunca desaparece — descoberta preservada. ──
  const pageRef = useRef<HTMLDivElement>(null);
  const [fabCollapsed, setFabCollapsed] = useState(false);
  const lastY = useRef(0);
  const idle = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => {
    const el = pageRef.current;
    if (!el) return;
    const onScroll = () => {
      const y = el.scrollTop;
      if (y > lastY.current + 4 && y > 48) setFabCollapsed(true);
      else if (y < lastY.current - 4) setFabCollapsed(false);
      lastY.current = y;
      clearTimeout(idle.current);
      idle.current = setTimeout(() => setFabCollapsed(false), 900);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => { el.removeEventListener('scroll', onScroll); clearTimeout(idle.current); };
  }, [pathname]);

  // ── Swipe entre tabs (à WhatsApp) com guards: ignora scrollers horizontais,
  //    inputs, sheets e o edge-back; exige gesto dominante-horizontal + limiar.
  //    Deslizar p/ ESQUERDA → tab à esquerda; p/ DIREITA → tab à direita. ──
  const swipe = useRef<{ x: number; y: number; blocked: boolean } | null>(null);
  const tabIdx = TABS.findIndex((t) => t.key === active);
  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    if (!t) { swipe.current = null; return; }
    const blocked = !!(e.target as HTMLElement).closest('[data-hscroll],.no-scrollbar,.tab-pills,.h-scroll,.stories-rail,input,textarea,.bottom-sheet')
      || t.clientX < 24 || t.clientX > window.innerWidth - 24;   // edge-back iOS
    swipe.current = { x: t.clientX, y: t.clientY, blocked };
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const s = swipe.current; swipe.current = null;
    if (!s || s.blocked || tabIdx < 0) return;
    const t = e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - s.x, dy = t.clientY - s.y;
    if (Math.abs(dx) < 64 || Math.abs(dx) < Math.abs(dy) * 1.6) return;  // limiar + dominante-horizontal
    const next = tabIdx + (dx < 0 ? -1 : 1);   // swipe-left → tab à esquerda
    const target = TABS[next];
    if (target) navigate(target.to);
  };

  return (
    <div className="app-shell">
      {/* Aurora — a cortina da marca. Duas frentes de onda: a de cima nasce sob
          o cabeçalho, a de baixo assenta na tabbar. São decoração pura (o brilho
          e o rebordo vêm dos pseudo-elementos da concha), por isso ficam fora da
          árvore de acessibilidade e fora da ordem de leitura. */}
      <span className="auw top" aria-hidden />
      <span className="auw" aria-hidden />
      {/* O mesmo salto do HQ: o cabeçalho e as ações do topo ficam para trás. */}
      <a className="skip-link" href="#conteudo">Saltar para o conteúdo</a>
      {/* Só para quem ouve: diz o nome da página a cada navegação. */}
      <p className="sr-only" role="status" aria-live="polite">{anuncio}</p>
      <header className="app-header">
        <span className="hdr-brand"><img src="/assets/symbol.png" alt="Marcianus" /></span>
        <div className="hdr-info">
          <span className="hdr-eyebrow">{header.eyebrow}</span>
          {/* O título da ROTA é o <h1> do ecrã. Não havia nenhum em 15 das 21
              rotas: para um leitor de ecrã a página não tinha nome, e a tecla
              «1» (saltar para o título) não ia a lado nenhum. Os títulos de
              herói dentro das páginas passam a <h2> — um só h1 por rota, sem
              casos especiais. O `.hdr-title` fixa o tipo e o reset global tira
              a margem, por isso nem um píxel muda. */}
          <h1 className="hdr-title">{header.title}</h1>
        </div>
        <div className="hdr-actions">
          <button className="icon-btn" aria-label="Notificações" onClick={() => navigate(ROUTES.notificacoes)}>
            <Icon name="bell" size={18} />
            {notifSummary && notifSummary.total > 0 && (
              <span className="badge-count" style={{ background: notifSummary.color }}>
                {notifSummary.total > 9 ? '9+' : notifSummary.total}
              </span>
            )}
          </button>
          {/* Perfil — único botão, à direita (Clerk avatar → sheet → Perfil/Bilhetes/Carteira) */}
          {!env.preview && <AuthControls />}
        </div>
      </header>

      {/* <main>: a região principal do ecrã. Era uma <div> — sem landmark, um
          leitor de ecrã não tinha como saltar a navegação nem saber onde
          começa o conteúdo (e o skip-link apontava para uma div anónima). */}
      <main className="page-area" id="conteudo" tabIndex={-1} ref={pageRef} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <Outlet />
      </main>

      {/* Hexis — FAB flutuante (glow + flutuação; scroll-aware) → rota /hexis. */}
      {!hexisTab && pathname !== ROUTES.hexis && (
        <button className={`hexis-fab${fabCollapsed ? ' collapsed' : ''}`} aria-label="Abrir Hexis" onClick={() => navigate(ROUTES.hexis)}>
          <HexisSymbol size={28} />
        </button>
      )}
      {hexisTab && <HexisPopup tab={hexisTab} onClose={closeHexis} />}

      <nav className="tabbar" aria-label="Navegação principal">
        {TABS.map((t) => (
          <NavLink
            key={t.key}
            to={t.to}
            aria-label={t.label}
            className={`tab-btn${t.fab ? ' fab-tab' : ''}${active === t.key ? ' active' : ''}`}
          >
            {t.fab ? (
              <span className="fab-ring"><TabGlyph icon={t.icon} /></span>
            ) : (
              <TabGlyph icon={t.icon} />
            )}
            <span>{t.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
